/**
 * Où en est la facturation d'une intervention : un repère court sur le planning,
 * repris sur la fiche. Absent quand rien n'est à signaler ou que la personne ne
 * voit pas les factures (le serveur ne transmet alors pas le statut).
 */
import type { ReactNode } from 'react';
import { ReceiptText } from 'lucide-react';
import { Badge, type BadgeTone } from '../../components/ui/Badge.js';

export type BillingStatus = 'a_facturer' | 'brouillon' | 'facturee' | 'payee' | 'contrat';

const BILLING: Record<BillingStatus, { label: string; short: string; tone: BadgeTone }> = {
  a_facturer: { label: 'À facturer', short: 'À facturer', tone: 'warn' },
  brouillon: { label: 'Facture en préparation', short: 'Brouillon', tone: 'neutral' },
  facturee: { label: 'Facturée, en attente de paiement', short: 'Facturée', tone: 'info' },
  payee: { label: 'Facturée et payée', short: 'Payée', tone: 'ok' },
  contrat: { label: 'Facturée avec le contrat', short: 'Contrat', tone: 'neutral' },
};

export function BillingBadge(props: {
  status: BillingStatus | null | undefined;
  compact?: boolean;
}): ReactNode {
  if (!props.status || !(props.status in BILLING)) return null;
  const { label, short, tone } = BILLING[props.status];
  return (
    <Badge
      tone={tone}
      title={`Facturation : ${label}`}
      icon={<ReceiptText size={props.compact ? 12 : 14} aria-hidden="true" />}
      {...(props.compact ? { compact: true } : {})}
    >
      {props.compact ? (
        <>
          <span aria-hidden="true">{short}</span>
          <span className="sr-only">Facturation : {label}</span>
        </>
      ) : (
        <span>{label}</span>
      )}
    </Badge>
  );
}
